import type { FormDetailResponse } from "@/types/entities/formality-request";
import type { Fields } from "../form-correction-table";
import { isEqual } from "@/utils/is-equal";

type Comment = { text: string; creator: { name: string; last_name: string } };

function tryParseJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  if (!value.startsWith("[") && !value.startsWith("{")) return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

type FieldCorrEntry = {
  filled_field_id: string;
  status?: string;
  value_before?: unknown;
  value_after?: unknown;
  was_observer?: boolean;
  value_changed?: boolean;
  comment?: string;
  comments?: Array<{
    text?: string;
    creator?: { name?: string; last_name?: string };
  }>;
  created_at?: string;
  changes?: {
    filled_field_id?: string;
    value_before?: unknown;
    value_after?: unknown;
    was_observer?: boolean;
    value_changed?: boolean;
  };
};

type CorrContext = {
  fc: FieldCorrEntry;
  correction: Record<string, unknown>;
  revision: Record<string, unknown>;
};

function buildAuthor(comment: Comment | undefined): string {
  return `${comment?.creator?.name || "Usuario desconocido"} ${comment?.creator?.last_name || ""}`.trim();
}

function resolveCorrectAuthor(
  fc: FieldCorrEntry,
  correction: Record<string, unknown>,
): string | null {
  if (fc.comments?.[0]?.creator) {
    const { name, last_name } = fc.comments[0].creator;
    return `${name || "Usuario"} ${last_name || ""}`.trim();
  }
  if (correction.created_by) {
    const createdBy = (correction as { created_by?: string }).created_by;
    return `Usuario ${createdBy}`;
  }
  return null;
}

function flattenFieldCorrections(
  formAssigment: FormDetailResponse["form_assignments"][number],
): CorrContext[] {
  const result: CorrContext[] = [];
  for (const revision of formAssigment.revisions ?? []) {
    for (const correction of revision.corrections ?? []) {
      const corrRecord = correction as Record<string, unknown>;
      const fcs: FieldCorrEntry[] =
        correction.field_corrections ??
        (corrRecord.fieldCorrections as FieldCorrEntry[]) ??
        [];
      for (const fc of fcs) {
        result.push({ fc, correction: corrRecord, revision: revision });
      }
    }
  }
  return result;
}

function buildFromFieldCorrections(
  field: Fields,
  formAssigment?: FormDetailResponse["form_assignments"][number],
): ChangeItem[] {
  if (!formAssigment) return [];

  const filledFieldIds = new Set(
    (field.filled_fields as unknown as FilledField[])?.map((ff) => ff.id) ?? [],
  );

  const changes: ChangeItem[] = [];
  let order = 0;

  const allFCs = flattenFieldCorrections(formAssigment);

  for (const { fc, correction, revision } of allFCs) {
    const filledFieldId = fc.filled_field_id ?? fc.changes?.filled_field_id;
    if (!filledFieldIds.has(filledFieldId)) continue;

    const rawValueBefore = fc.value_before ?? fc.changes?.value_before;
    const rawValueAfter = fc.value_after ?? fc.changes?.value_after;
    if (rawValueBefore == null && rawValueAfter == null) continue;

    const wasObserver = fc.was_observer ?? fc.changes?.was_observer ?? false;
    const valueChanged = fc.value_changed ?? fc.changes?.value_changed ?? false;
    if (!wasObserver || !valueChanged) continue;

    const valueBefore = tryParseJson(rawValueBefore ?? null);
    const valueAfter = tryParseJson(rawValueAfter ?? null);
    if (isEqual(valueBefore, valueAfter)) continue;

    order++;
    const change: ChangeItem = {
      order,
      oldFieldId: filledFieldId,
      newFieldId: filledFieldId,
      field_id: filledFieldId,
      oldValue: (valueBefore ?? "") as ChangeItem["oldValue"],
      newValue: (valueAfter ?? "") as ChangeItem["newValue"],
      comment: fc.comments?.[0]?.text || fc.comment || null,
      changedAt: new Date(
        (fc.created_at ??
          correction.created_at ??
          revision.created_at) as string,
      ),
      author: resolveCorrectAuthor(fc, correction),
      was_observer: wasObserver,
      value_changed: valueChanged,
    };
    changes.push(change);
  }

  return changes.reverse();
}

function resolveNewField(sorted: FilledField[], i: number): FilledField {
  const next = sorted[i + 1];
  if (!sorted[i].correction || next.correction) return next;

  for (let j = i + 2; j < sorted.length; j++) {
    if (sorted[j].correction) return sorted[j];
  }

  return (
    sorted
      .slice(i + 1)
      .sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      )
      .find((ff) => ff.value != null && ff.value !== "") ?? sorted.at(-1)!
  );
}

export type FilledField = {
  id: string;
  value: string;
  label?: string;
  name?: string;
  created_at: string;
  updated_at: string;
  field_id: string;
  correction?: {
    comments?: Comment[];
    status: "APPROVED" | "DENIED";
  } | null;
};

export type ChangeItem = {
  oldFieldId: string;
  newFieldId: string;
  oldValue: string | { [x: string]: string };
  newValue: string | { [x: string]: string };
  comment: string | null;
  changedAt: Date;
  author: string | null;
  order: number;
  field_id: string;
  was_observer: boolean;
  value_changed: boolean;
};

/**
 * Mirrors the empty state of `FieldHistoryModal`: sin cambios no hay nada que
 * abrir, así que quien muestre el acceso al historial puede ocultarlo.
 */
export function hasFieldChangeHistory(
  field: Fields,
  formAssigment?: FormDetailResponse["form_assignments"][number],
): boolean {
  return buildFieldChangeHistory(field, formAssigment).length > 0;
}

export function buildFieldChangeHistory(
  field: Fields,
  formAssigment?: FormDetailResponse["form_assignments"][number],
): ChangeItem[] {
  // Try new approach: use field_corrections with value_before/value_after from revisions
  const hasFieldCorrectionsWithValues = (formAssigment?.revisions ?? []).some(
    (r) =>
      (r.corrections ?? []).some((c) => {
        const fcs =
          (c as Record<string, unknown>).field_corrections ??
          (c as Record<string, unknown>).fieldCorrections;
        if (!Array.isArray(fcs)) return false;
        return fcs.some((fc: FieldCorrEntry) => {
          const vb = fc.value_before ?? fc.changes?.value_before;
          const va = fc.value_after ?? fc.changes?.value_after;
          return vb != null || va != null;
        });
      }),
  );
  const fromFieldCorrections = buildFromFieldCorrections(field, formAssigment);
  if (hasFieldCorrectionsWithValues) return fromFieldCorrections;

  // Fallback: compare filled_fields (legacy approach)
  const filledFields = field.filled_fields as unknown as FilledField[];
  if (field.type === "interoperability") {
    return buildInteroperabilityChangeHistory(field, formAssigment);
  }

  if (!Array.isArray(filledFields) || filledFields.length < 2) return [];

  const sorted = [...filledFields].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  const changes: ChangeItem[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const oldFF = sorted[i];
    const newFF = resolveNewField(sorted, i);

    const oldValue = Array.isArray(oldFF.value)
      ? oldFF.value
      : tryParseJson(oldFF.value ?? null);
    const newValue = Array.isArray(newFF.value)
      ? newFF.value
      : tryParseJson(newFF.value ?? null);

    if (isEqual(oldValue, newValue)) continue;

    const comment = oldFF.correction?.comments?.[0];

    changes.push({
      order: i + 1,
      oldFieldId: oldFF.id,
      newFieldId: newFF.id,
      field_id: oldFF.id,
      oldValue: oldValue as ChangeItem["oldValue"],
      newValue: newValue as ChangeItem["newValue"],
      comment: comment?.text ?? null,
      changedAt: new Date(newFF.created_at),
      author: buildAuthor(comment),
      was_observer: false,
      value_changed: true,
    });
  }

  return changes.sort((a, b) => b.order - a.order);
}

const buildInteroperabilityChangeHistory = (
  field: Fields,
  formAssigment?: FormDetailResponse["form_assignments"][number],
): ChangeItem[] => {
  const filledFields = field.filled_fields as unknown as FilledField[];

  // for interoperability, if the interoperability has more 2 or more inputs, then there will be 2 or more filledFields representing each input,
  // so if has 2 inputs, there will be 2 filledFields, if has 3 inputs, there will be 3 filledFields, and so on.
  // will need to change the logic to compare the filledFields in pairs, so if has 2 inputs, will compare the first with the second, if has 3 inputs, will compare the first with the second, then the second with the third, and so on.
  const inputCount =
    (field.config as unknown as { input_field?: unknown[] })?.input_field
      ?.length || 1;

  if (!Array.isArray(filledFields) || filledFields.length < inputCount * 2)
    return [];

  // Group filledFields into chunks of inputCount — each chunk is one form submission
  const groups: FilledField[][] = [];
  for (let i = 0; i + inputCount <= filledFields.length; i += inputCount) {
    groups.push(filledFields.slice(i, i + inputCount));
  }

  const changes: ChangeItem[] = [];
  for (let i = 0; i < groups.length - 1; i++) {
    const oldGroup = groups[i];
    const newGroup = groups[i + 1];
    const comment = formAssigment?.revisions
      .flatMap((r) => r.corrections)
      .flatMap((c) => c.field_corrections)
      .find(
        (fc) =>
          fc.filled_field_id === oldGroup[0].id ||
          fc.filled_field_id === newGroup[0].id,
      )?.comments?.[0];
    const oldValue = oldGroup.reduce(
      (acc, ff) => {
        acc[ff.name?.split("__")?.[1] || ff.field_id] = ff.value;
        return acc;
      },
      {} as { [x: string]: string },
    );
    const newValue = newGroup.reduce(
      (acc, ff) => {
        acc[ff.name?.split("__")?.[1] || ff.field_id] = ff.value;
        return acc;
      },
      {} as { [x: string]: string },
    );
    changes.push({
      order: i + 1,
      oldFieldId: oldGroup?.[0].id, // assuming the first filledField in the group represents the whole interoperability field
      newFieldId: newGroup?.[0].id,
      field_id: oldGroup?.[0]?.field_id || "",
      oldValue: oldValue,
      newValue: newValue,
      comment: comment?.text ?? null,
      changedAt: new Date(newGroup?.[0]?.created_at),
      author: buildAuthor(comment),
      was_observer: false,
      value_changed: true,
    });
  }

  return changes.sort((a, b) => b.order - a.order);
};
